import Pencil from 'lucide-react/dist/esm/icons/pencil';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { getReviewImageUrl } from '@/lib/image-urls';
import StarRating from './star-rating';

interface ReviewMedia {
  id: string;
  userId: string;
  filename: string;
  s3Key: string;
  itemUin: number;
  reviewId: string | null;
  mimeType: string;
  createdAt: string;
  updatedAt: string;
}

interface Review {
  id: string;
  itemUin: number;
  rating: number;
  title: string | null;
  content: string;
  createdAt: string;
  updatedAt: string;
  userId: string;
  userName: string;
  media: ReviewMedia[];
  itemName?: string;
}

interface ReviewCardProps {
  review: Review & { itemName?: string };
  canEdit: boolean;
  showUserInfo?: boolean;
  showItemName?: boolean;
  onEdit: (review: Review) => void;
  onDelete: (review: Review) => void;
  onImageClick: (images: string[], index: number) => void;
  onItemClick?: (review: Review) => void;
}

function getDisplayName(review: Review): string {
  return review.userName || 'משתמש אנונימי';
}

function getInitials(review: Review): string {
  const name = review.userName;
  if (name) {
    const words = name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  }
  return '?';
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('he-IL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function ReviewCard({
  review,
  canEdit,
  showUserInfo = true,
  showItemName = false,
  onEdit,
  onDelete,
  onImageClick,
  onItemClick,
}: ReviewCardProps) {
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.currentTarget;
    target.style.border = '2px solid red';
    target.alt = 'Failed to load image';
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-3 flex flex-row-reverse items-start justify-between">
          {showUserInfo ? (
            <div className="flex flex-row-reverse items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-sm font-medium text-white">
                {getInitials(review)}
              </div>
              <div className="text-right">
                <div className="font-medium text-gray-900 dark:text-white">
                  {getDisplayName(review)}
                </div>
                <div className="text-sm text-gray-500">{formatDate(review.createdAt)}</div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col text-right">
              {showItemName &&
                review.itemName &&
                (onItemClick ? (
                  <button
                    type="button"
                    className="mb-1 text-right font-medium text-gray-900 transition-colors hover:text-primary dark:text-white"
                    onClick={() => onItemClick?.(review)}
                  >
                    {review.itemName}
                  </button>
                ) : (
                  <div className="mb-1 font-medium text-gray-900 dark:text-white">
                    {review.itemName}
                  </div>
                ))}
              <div className="text-sm text-gray-500">{formatDate(review.createdAt)}</div>
            </div>
          )}

          {canEdit && (
            <div className="flex flex-row-reverse gap-2">
              <Button variant="ghost" size="sm" onClick={() => onDelete(review)}>
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onEdit(review)}>
                <Pencil className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        <div className="mb-3">
          <div className="mb-2 flex flex-row-reverse items-center gap-2">
            <StarRating rating={review.rating} size="sm" />
          </div>
          <p className="mb-3 text-right leading-relaxed text-gray-700 dark:text-gray-300">
            {review.content}
          </p>

          {/* Review Images */}
          {review.media && review.media.length > 0 && (
            <div className="flex flex-row-reverse flex-wrap gap-2">
              {review.media.map((item, index) => {
                // Handle both string keys and ReviewMedia objects
                const key = typeof item === 'string' ? item : item.s3Key;
                if (!key) return null;

                const imageUrl = getReviewImageUrl(key);
                if (!imageUrl) return null;

                const uniqueKey = typeof item === 'string' ? item : item.id || item.s3Key;

                // Get all keys for lightbox
                const allKeys =
                  review.media?.map((m) => (typeof m === 'string' ? m : m.s3Key)).filter(Boolean) ||
                  [];

                return (
                  <div key={uniqueKey} className="group relative">
                    <img
                      src={imageUrl}
                      alt="Review"
                      className="h-20 w-20 cursor-pointer rounded-lg border border-gray-200 object-cover transition-opacity hover:opacity-80 dark:border-gray-600"
                      loading="lazy"
                      onClick={() => onImageClick(allKeys, index)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          onImageClick(allKeys, index);
                        }
                      }}
                      onError={handleImageError}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
